import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ThemeToggle from '../components/ThemeToggle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Scale, Mail, Lock, User, Building2, Globe } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [firmName, setFirmName] = useState('');
  const [selectedPlan] = useState('unified');
  // Billing cycle + seat count for the unified plan.
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [seats, setSeats] = useState(1);
  const [referralCode, setReferralCode] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  // Preselect cycle + seats when coming from the landing CTA
  // (?cycle=monthly|annual&users=N) and force the register form when
  // ?mode=register is present, so a single click on 'Start trial' from
  // the landing puts the visitor straight on the signup form.
  useEffect(() => {
    if (searchParams.get('mode') === 'register') setIsLogin(false);
    const c = searchParams.get('cycle');
    if (c === 'annual' || c === 'monthly') setBillingCycle(c);
    const u = parseInt(searchParams.get('users') || '', 10);
    if (Number.isFinite(u) && u > 0) setSeats(Math.min(9999, u));
    const refCode = searchParams.get('ref');
    if (refCode) setReferralCode(refCode.trim().toUpperCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // MFA challenge state: set after login when user has 2FA enabled
  const [mfaChallenge, setMfaChallenge] = useState(null); // { challenge, email }
  const [mfaCode, setMfaCode] = useState('');
  const { login, register, loginWithGoogle, completeMfa } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const res = await login(email, password);
        if (res?.mfa_required) {
          setMfaChallenge({ challenge: res.challenge, email: res.email });
          setLoading(false);
          return;
        }
        toast.success(language === 'en' ? 'Welcome back!' : '¡Bienvenido de nuevo!');
        navigate('/dashboard');
      } else {
        if (!privacyAccepted) {
          toast.error(language === 'es'
            ? 'Debe aceptar la politica de privacidad para continuar.'
            : 'You must accept the privacy policy to continue.');
          setLoading(false);
          return;
        }
        await register(name, email, password, firmName, selectedPlan, {
          privacy_accepted: true,
          privacy_version: '1.0',
          billing_cycle: billingCycle,
          seats,
          referral_code: referralCode || undefined,
        });
        toast.success(language === 'en'
          ? 'Account created! 14-day free trial started.'
          : '¡Cuenta creada! Prueba gratis de 14 dias iniciada.');
        // All paying tenants (unified plan) need a card to start the
        // trial, so land them on the billing tab to configure it.
        navigate('/dashboard/settings?tab=billing');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaChallenge) return;
    setLoading(true);
    try {
      await completeMfa(mfaChallenge.challenge, mfaCode);
      toast.success(language === 'en' ? 'Welcome back!' : '¡Bienvenido de nuevo!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo & Language Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-navy rounded-sm flex items-center justify-center">
                <Scale className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-serif font-bold text-navy">ERP Legal</span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="gap-2"
                data-testid="login-language-toggle"
              >
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'ES' : 'EN'}
              </Button>
            </div>
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl lg:text-4xl font-serif font-bold text-navy">
              {isLogin ? t('auth.welcomeBack') : t('auth.createAccount')}
            </h1>
            <p className="text-muted-foreground">
              {isLogin ? t('auth.enterCredentials') : t('auth.startManaging')}
            </p>
          </div>

          {/* Form */}
          {mfaChallenge ? (
            <form onSubmit={handleMfaSubmit} className="space-y-6" data-testid="mfa-form">
              <div className="space-y-2">
                <h2 className="text-xl font-serif font-semibold text-foreground">
                  {language === 'es' ? 'Codigo de verificacion' : 'Verification code'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {language === 'es'
                    ? `Ingrese el codigo de 6 digitos de su app autenticadora para ${mfaChallenge.email}.`
                    : `Enter the 6-digit code from your authenticator app for ${mfaChallenge.email}.`}
                </p>
              </div>
              <div>
                <Label htmlFor="mfa-code">
                  {language === 'es' ? 'Codigo' : 'Code'}
                </Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  pattern="[0-9 ]*"
                  maxLength={7}
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder={t('authPh.mfaCodePlaceholder')}
                  className="font-mono text-lg tracking-widest text-center"
                />
              </div>
              <Button type="submit" className="btn-accent w-full" disabled={loading}>
                {loading
                  ? (language === 'es' ? 'Verificando...' : 'Verifying...')
                  : (language === 'es' ? 'Verificar' : 'Verify')}
              </Button>
              <button
                type="button"
                onClick={() => { setMfaChallenge(null); setMfaCode(''); }}
                className="block w-full text-sm text-muted-foreground hover:text-foreground"
              >
                {language === 'es' ? 'Volver al inicio de sesion' : 'Back to sign in'}
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">{t('auth.fullName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      data-testid="name-input"
                      type="text"
                      placeholder={t('authPh.fullNamePlaceholder')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 rounded-none"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="firmName">{t('auth.lawFirmName')}</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="firmName"
                      data-testid="firm-name-input"
                      type="text"
                      placeholder={t('authPh.firmNamePlaceholder')}
                      value={firmName}
                      onChange={(e) => setFirmName(e.target.value)}
                      className="pl-10 rounded-none"
                    />
                  </div>
                </div>

                <div className="space-y-3 rounded-md border border-border bg-card p-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      {language === 'es' ? 'Plan (14 dias gratis)' : 'Plan (14-day free trial)'}
                    </Label>
                    <p className="mt-1 text-sm font-medium text-foreground">
                      {language === 'es' ? 'ERP Legal Unificado' : 'ERP Legal Unified'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {language === 'es'
                        ? 'Casos, facturación, fideicomisos, IA y más. $20 por usuario al mes (20% menos si paga anual).'
                        : 'Cases, billing, trust, AI, and more. $20 per user/month (20% off if you pay yearly).'}
                    </p>
                  </div>

                  {/* Billing cycle */}
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {language === 'es' ? 'Ciclo de facturacion' : 'Billing cycle'}
                    </Label>
                    <div className="mt-1 inline-flex rounded border border-border p-0.5 bg-background">
                      <button
                        type="button"
                        onClick={() => setBillingCycle('monthly')}
                        className={`px-3 py-1 text-xs rounded transition-all ${
                          billingCycle === 'monthly'
                            ? 'bg-navy text-white font-medium'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="register-cycle-monthly"
                      >
                        {language === 'es' ? 'Mensual' : 'Monthly'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setBillingCycle('annual')}
                        className={`px-3 py-1 text-xs rounded transition-all flex items-center gap-1 ${
                          billingCycle === 'annual'
                            ? 'bg-navy text-white font-medium'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="register-cycle-annual"
                      >
                        {language === 'es' ? 'Anual' : 'Annual'}
                        <span className={`text-[9px] px-1 rounded font-bold ${
                          billingCycle === 'annual' ? 'bg-white text-navy' : 'bg-emerald text-white'
                        }`}>−20%</span>
                      </button>
                    </div>
                  </div>

                  {/* Seats */}
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {language === 'es' ? 'Cantidad de usuarios' : 'Number of users'}
                    </Label>
                    <div className="mt-1 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSeats((n) => Math.max(1, Number(n) - 1))}
                        className="w-8 h-8 rounded border border-border hover:bg-muted text-base leading-none"
                        aria-label="decrement"
                      >−</button>
                      <Input
                        type="number"
                        min="1"
                        max="9999"
                        value={seats}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setSeats(Number.isFinite(v) && v > 0 ? v : 1);
                        }}
                        className="w-20 h-8 text-center rounded-none"
                        data-testid="register-seats-input"
                      />
                      <button
                        type="button"
                        onClick={() => setSeats((n) => Math.min(9999, Number(n) + 1))}
                        className="w-8 h-8 rounded border border-border hover:bg-muted text-base leading-none"
                        aria-label="increment"
                      >+</button>
                      <span className="text-xs text-muted-foreground ml-2">
                        {(() => {
                          const perYr = 20 * 12 * 0.8; // 192
                          const perMo = 20;
                          const total = billingCycle === 'annual' ? perYr * seats : perMo * seats;
                          const suffix = billingCycle === 'annual'
                            ? (language === 'es' ? '/ano' : '/year')
                            : (language === 'es' ? '/mes' : '/month');
                          return `= $${Math.round(total).toLocaleString()} ${suffix}`;
                        })()}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {language === 'es'
                      ? 'Le pediremos una tarjeta al final de la prueba. Puede cancelar antes del día 14 sin cargo.'
                      : 'We\'ll ask for a card at the end of the trial. Cancel before day 14 at no charge.'}
                  </p>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder={t('authPh.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-none"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder={t('authPh.passwordDots')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 rounded-none"
                  required
                  minLength={8}
                  maxLength={16}
                />
              </div>
              {!isLogin && password && (
                <PasswordStrength password={password} language={language} />
              )}
            </div>

            {!isLogin && (
              <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 accent-bronze"
                  data-testid="privacy-checkbox"
                />
                <span>
                  {language === 'es' ? 'He leido y acepto la ' : 'I have read and accept the '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-bronze underline hover:text-bronze/80"
                  >
                    {language === 'es' ? 'Politica de Privacidad' : 'Privacy Policy'}
                  </a>
                  {language === 'es'
                    ? ', incluyendo la retencion de datos de 120 dias tras la cancelacion.'
                    : ', including 120-day data retention after cancellation.'}
                </span>
              </label>
            )}

            <Button
              type="submit"
              data-testid="submit-btn"
              className="w-full btn-primary h-12"
              disabled={loading || (!isLogin && !privacyAccepted)}
            >
              {loading ? t('common.pleaseWait') : isLogin ? t('auth.signInBtn') : t('auth.createAccountBtn')}
            </Button>
          </form>
          )}

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-background text-muted-foreground">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            data-testid="google-login-btn"
            variant="outline"
            className="w-full h-12 rounded-none"
            onClick={loginWithGoogle}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {t('auth.continueGoogle')}
          </Button>

          {/* Toggle */}
          <p className="text-center text-sm text-muted-foreground">
            {isLogin ? t('auth.noAccount') : t('auth.haveAccount')}{' '}
            <button
              type="button"
              data-testid="toggle-auth-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-bronze font-medium hover:underline"
            >
              {isLogin ? t('auth.signUp') : t('nav.signIn')}
            </button>
          </p>

          {/* Back to home */}
          <Link
            to="/"
            data-testid="back-home-link"
            className="block text-center text-sm text-muted-foreground hover:text-foreground"
          >
            {t('auth.backHome')}
          </Link>
        </div>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1596574027151-2ce81d85af3e?w=1200"
            alt="Legal consultation"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 p-16 text-center">
          <blockquote className="text-2xl lg:text-3xl font-serif text-white/90 italic leading-relaxed max-w-lg">
            {t('auth.quote')}
          </blockquote>
          <p className="mt-6 text-white/70 font-medium">{t('auth.quoteAuthor')}</p>
        </div>
      </div>
    </div>
  );
}

function PasswordStrength({ password, language }) {
  const lbl = (es, en) => (language === 'es' ? es : en);
  const checks = [
    { pass: password.length >= 8, label: lbl('8 caracteres mínimo', '8 characters minimum') },
    { pass: password.length <= 16, label: lbl('16 caracteres máximo', '16 characters maximum') },
    { pass: /[A-Z]/.test(password), label: lbl('Una mayúscula', 'One uppercase letter') },
    { pass: /[a-z]/.test(password), label: lbl('Una minúscula', 'One lowercase letter') },
    { pass: /\d/.test(password), label: lbl('Un número', 'One digit') },
    { pass: /[^A-Za-z0-9]/.test(password), label: lbl('Un carácter especial (!@#...)', 'One special character (!@#...)') },
  ];
  const passCount = checks.filter((c) => c.pass).length;

  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full ${
              i < passCount
                ? passCount <= 2 ? 'bg-destructive'
                : passCount <= 4 ? 'bg-bronze'
                : 'bg-emerald'
                : 'bg-muted'
            }`}
          />
        ))}
      </div>
      <ul className="text-[11px] text-muted-foreground space-y-0.5">
        {checks.map((c) => (
          <li key={c.label} className={c.pass ? 'text-emerald' : ''}>
            {c.pass ? '✓' : '○'} {c.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
